import { useEffect, useState } from "react";
import { calculateEstimateTotals } from "../../../pricing/calculateEstimateTotals";

function formatCurrency(value, currency = "DKK") {
  return new Intl.NumberFormat(currency === "SEK" ? "sv-SE" : "da-DK", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(Number(value || 0));
}

export default function SummaryPanel({
  summary = {},
  estimate = {},
  onChangeMarginPercent,
}) {
  const [showInactive, setShowInactive] = useState(false);

  const calculatedSummary = calculateEstimateTotals({
    ...estimate,
    summary: {
      ...(estimate?.summary || {}),
      ...(summary || {}),
    },
  });

  const currency =
    estimate?.meta?.currency || estimate?.summary?.currency || "DKK";

  const activeRowsCount = calculatedSummary.activeRowsCount;
  const inactiveRowsCount = calculatedSummary.inactiveRowsCount;

  const activeBaseCost = calculatedSummary.baseCost;
  const inactiveTotal = calculatedSummary.inactiveTotal;

  const marginPercent = calculatedSummary.marginPercent;
  const marginAmount = calculatedSummary.marginAmount;

  const subtotal = calculatedSummary.subtotal;

  const vatRate = calculatedSummary.vatRate;
  const vat = calculatedSummary.vat;

  const total = calculatedSummary.total;

  return (
    <section className="border-b border-[#edebe9] bg-white">
      <div className="flex items-center justify-between border-b border-[#edebe9] bg-[#faf9f8] px-4 py-2">
        <h3 className="text-sm font-semibold text-[#201f1e]">Pricing</h3>

        <span className="text-xs text-[#605e5c]">
          {activeRowsCount} active
        </span>
      </div>

      <div className="p-4">
        <div className="space-y-2 text-sm">
          <PriceRow
            label="Base cost"
            value={formatCurrency(activeBaseCost, currency)}
          />

          <MarginRow
            value={marginPercent}
            amount={formatCurrency(marginAmount, currency)}
            onChange={onChangeMarginPercent}
          />

          <Divider />

          <PriceRow
            label="Subtotal"
            value={formatCurrency(subtotal, currency)}
            strong
          />

          <PriceRow
            label={`VAT ${Math.round(vatRate * 100)}%`}
            value={formatCurrency(vat, currency)}
          />
        </div>

        <div className="mt-4 rounded-sm bg-[#201f1e] px-4 py-4 text-white">
          <div className="text-xs font-medium uppercase tracking-wide text-[#c8c6c4]">
            Total incl. VAT
          </div>

          <div className="mt-1 text-2xl font-semibold tabular-nums">
            {formatCurrency(total, currency)}
          </div>
        </div>

        {inactiveRowsCount > 0 && (
          <div className="mt-4 border-t border-[#edebe9] pt-3">
            <button
              type="button"
              onClick={() => setShowInactive((value) => !value)}
              className="flex w-full items-center justify-between text-xs text-[#605e5c] hover:text-[#201f1e]"
            >
              <span>Inactive rows · {inactiveRowsCount}</span>
              <span>{formatCurrency(inactiveTotal, currency)}</span>
            </button>

            {showInactive && (
              <div className="mt-2 rounded-sm bg-[#faf9f8] px-3 py-2">
                <PriceRow
                  label="Excluded"
                  value={formatCurrency(inactiveTotal, currency)}
                  muted
                />
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

function MarginRow({ value, amount, onChange }) {
  const [draftValue, setDraftValue] = useState(String(value ?? 0));

  useEffect(() => {
    setDraftValue(String(value ?? 0));
  }, [value]);

  function commit(nextValue) {
    const numeric = Number(nextValue);

    if (!Number.isFinite(numeric)) return;

    const safeValue = Math.max(0, Math.min(100, numeric));
    onChange?.(safeValue);
  }

  return (
    <div className="flex items-center justify-between gap-4">
      <span className="text-[#605e5c]">Margin</span>

      <div className="flex items-center gap-2">
        <input
          type="number"
          min="0"
          max="100"
          value={draftValue}
          onChange={(event) => {
            setDraftValue(event.target.value);
            commit(event.target.value);
          }}
          onBlur={() => {
            if (draftValue === "") {
              setDraftValue("0");
              commit(0);
            }
          }}
          className="h-7 w-16 rounded-sm border border-[#d2d0ce] bg-white px-2 text-right text-sm tabular-nums text-[#201f1e] focus:border-[#0f6cbd] focus:outline-none"
        />

        <span className="text-sm text-[#605e5c]">%</span>

        <span className="w-24 text-right font-medium tabular-nums text-[#107c10]">
          {amount}
        </span>
      </div>
    </div>
  );
}

function PriceRow({ label, value, strong = false, positive = false, muted = false }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span
        className={[
          muted ? "text-[#8a8886]" : "text-[#605e5c]",
          strong ? "font-medium text-[#201f1e]" : "",
        ].join(" ")}
      >
        {label}
      </span>

      <span
        className={[
          "text-right font-medium tabular-nums",
          muted ? "text-[#8a8886]" : "text-[#323130]",
          strong ? "text-[#201f1e]" : "",
          positive ? "text-[#107c10]" : "",
        ].join(" ")}
      >
        {value}
      </span>
    </div>
  );
}

function Divider() {
  return <div className="my-2 border-t border-[#edebe9]" />;
}